// Accessibility button, bottom left of every page: opens a panel of settings, grouped as
//   Reading      text size · text spacing · dyslexia-friendly font · underline links · strong focus ring · read aloud (+ speed) · reading ruler
//   Seeing       high contrast · greyscale · hide decorations · reduce motion
//   Layout       narrow view ("reflow": the phone layout in one centred column, phone width) · bigger tap targets
// The choices live in M3Core (apps/shared/m3-core.js: stored on the device, applied before the page paints, shared by every app, CSS in m3.css)
// and the panel only edits them. With "Read aloud" on, a speaker button appears above this one. window.useCompactLayout() is true in the phone
// layout or the narrow view (for code that reads the window width).
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect, useRef } = React;

  window.useCompactLayout = () => {
    const media = MaterialUI.useMediaQuery('(max-width:899.95px)');
    const [narrow, setNarrow] = useState(() => window.M3Core.getA11y().narrow > 0);
    useEffect(() => {
      const on = () => setNarrow(window.M3Core.getA11y().narrow > 0);
      window.addEventListener('wb-a11y', on);
      return () => window.removeEventListener('wb-a11y', on);
    }, []);
    return media || narrow;
  };

  const ease = 'var(--m3-duration-medium) var(--m3-easing-emphasized-decelerate)';
  const range = { width: '100%', height: 28, m: 0, accentColor: 'var(--m3-primary)', cursor: 'pointer' };
  const colWidth = n => Math.round(900 - (n / 100) * (900 - 380));
  const speechOk = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

  // A Material 3 switch (52 x 32): a button with role "switch"
  function Toggle({ checked, onChange, label }) {
    return (
      <Box component="button" type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
        sx={{ position: 'relative', flex: '0 0 auto', width: 52, height: 32, p: 0, border: `2px solid ${checked ? 'var(--m3-primary)' : 'var(--m3-outline)'}`, borderRadius: 16, cursor: 'pointer', backgroundColor: checked ? 'var(--m3-primary)' : 'var(--m3-surface-container-highest)', transition: `background-color ${ease}`, '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2 } }}>
        <Box component="span" sx={{ position: 'absolute', top: '50%', left: checked ? 24 : 4, width: checked ? 24 : 16, height: checked ? 24 : 16, transform: 'translateY(-50%)', borderRadius: '50%', backgroundColor: checked ? 'var(--m3-on-primary)' : 'var(--m3-outline)', transition: `left ${ease}, width ${ease}, height ${ease}` }} />
      </Box>
    );
  }
  // A switch with its title and a line of explanation
  function Switch({ title, note, checked, onChange }) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" component="h4" sx={{ m: 0, fontWeight: 600, color: 'var(--m3-on-surface)' }}>{title}</Typography>
          {note && <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>{note}</Typography>}
        </Box>
        <Toggle checked={checked} onChange={onChange} label={title} />
      </Box>
    );
  }
  function Row({ title, value, note, children }) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
          <Typography variant="subtitle2" component="h4" sx={{ m: 0, fontWeight: 600, color: 'var(--m3-on-surface)' }}>{title}</Typography>
          {value && <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)' }}>{value}</Typography>}
        </Box>
        {children}
        {note && <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>{note}</Typography>}
      </Box>
    );
  }
  const Group = ({ title, children }) => (
    <Box component="section" aria-label={title} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="overline" component="h3" sx={{ m: 0, letterSpacing: '0.08em', color: 'var(--m3-primary)', fontWeight: 600 }}>{title}</Typography>
      {children}
    </Box>
  );

  // Reading ruler: the page is dimmed except a band that follows the pointer (and the keyboard focus)
  function Ruler() {
    const ref = useRef(null);
    useEffect(() => {
      const set = y => { if (ref.current) ref.current.style.setProperty('--ry', `${Math.round(y)}px`); };
      set(window.innerHeight / 2);
      let raf = 0;
      const move = e => { if (e.pointerType === 'touch') return; cancelAnimationFrame(raf); raf = requestAnimationFrame(() => set(e.clientY)); };
      const focus = e => { if (e.target && e.target.getBoundingClientRect) { const r = e.target.getBoundingClientRect(); set(r.top + r.height / 2); } };
      window.addEventListener('pointermove', move, { passive: true });
      window.addEventListener('focusin', focus);
      return () => { window.removeEventListener('pointermove', move); window.removeEventListener('focusin', focus); cancelAnimationFrame(raf); };
    }, []);
    const band = '2.6rem', dim = 'color-mix(in srgb, black 52%, transparent)';
    return <Box ref={ref} aria-hidden="true" sx={{ position: 'fixed', inset: 0, zIndex: 1140, pointerEvents: 'none', background: `linear-gradient(to bottom, ${dim} 0, ${dim} calc(var(--ry, 50vh) - ${band}), transparent calc(var(--ry, 50vh) - ${band}), transparent calc(var(--ry, 50vh) + ${band}), ${dim} calc(var(--ry, 50vh) + ${band}), ${dim} 100%)` }} />;
  }

  function AccessibilityFab({ user }) {
    const { Fab, GoogleIcon, IconButton, Button, Tooltip } = window.Components;
    const M = window.M3Core;
    const [open, setOpen] = useState(false);
    const [s, setS] = useState(() => M.getA11y());
    const [reduced, setReduced] = useState(() => M.reducedMotion());
    const [speaking, setSpeaking] = useState(false);
    const panelRef = useRef(null);
    const fabRef = useRef(null);
    useEffect(() => {
      const on = () => { setS(M.getA11y()); setReduced(M.reducedMotion()); };
      window.addEventListener('wb-a11y', on);
      return () => window.removeEventListener('wb-a11y', on);
    }, []);
    // Escape or a click outside closes it; focus goes into the panel when it opens and back to the button when it closes
    useEffect(() => {
      if (!open) return undefined;
      const onKey = e => { if (e.key === 'Escape') { setOpen(false); if (fabRef.current) fabRef.current.focus(); } };
      const onDown = e => { if (panelRef.current && !panelRef.current.contains(e.target) && !(fabRef.current && fabRef.current.contains(e.target))) setOpen(false); };
      document.addEventListener('keydown', onKey);
      document.addEventListener('pointerdown', onDown);
      if (panelRef.current) panelRef.current.focus();
      return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
    }, [open]);
    const set = patch => M.setA11y(patch);

    // Read aloud: the selection if there is one, else the page's main content, in sentence-sized pieces (browsers cut off long utterances)
    const stop = () => { if (speechOk) window.speechSynthesis.cancel(); setSpeaking(false); };
    const read = () => {
      if (!speechOk) return;
      if (speaking) { stop(); return; }
      const sel = String(window.getSelection() || '').trim();
      const main = document.querySelector('main');
      const text = (sel || (main ? main.innerText : document.body.innerText)).replace(/\s+/g, ' ').trim();
      if (!text) return;
      const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || [text];
      const chunks = []; let cur = '';
      for (const p of sentences) { if (cur && (cur + p).length > 220) { chunks.push(cur); cur = p; } else cur += p; }
      if (cur) chunks.push(cur);
      window.speechSynthesis.cancel();
      chunks.forEach((c, i) => {
        const u = new window.SpeechSynthesisUtterance(c);
        u.rate = s.speed; u.lang = document.documentElement.lang || 'en';
        if (i === chunks.length - 1) { u.onend = () => setSpeaking(false); u.onerror = () => setSpeaking(false); }
        window.speechSynthesis.speak(u);
      });
      setSpeaking(true);
    };
    useEffect(() => { if (!s.readAloud) stop(); }, [s.readAloud]);
    useEffect(() => () => { if (speechOk) window.speechSynthesis.cancel(); }, []);

    const contrast = s.contrast == null ? (window.matchMedia && window.matchMedia('(prefers-contrast: more)').matches ? 0.5 : 0) : s.contrast;
    const changed = JSON.stringify(s) !== JSON.stringify(window.M3Core.defaults);
    const left = pad => `calc(max(0px, 50% - var(--wb-column, 100vw) / 2) + ${pad}px)`;

    return (
      <>
        {s.ruler && <Ruler />}
        <Box component="aside" aria-label="Accessibility tools" sx={{
          position: 'fixed', left: { xs: left(16), md: left(24) }, zIndex: 1150, display: 'flex', flexDirection: 'column-reverse', alignItems: 'flex-start', gap: 1.5,
          bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px) + 16px)', md: 88 }, pointerEvents: 'none', '& > *': { pointerEvents: 'auto' }
        }}>
          <Tooltip title="Accessibility" placement="right">
            <Fab ref={fabRef} size="small" tone="secondary" icon={<GoogleIcon name="accessibility_new" size={22} />} aria-label="Accessibility settings" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen(o => !o)} />
          </Tooltip>
          {s.readAloud && speechOk && (
            <Tooltip title={speaking ? 'Stop reading' : 'Read aloud (the selection, or this page)'} placement="right">
              <Fab size="small" tone="tertiary" icon={<GoogleIcon name={speaking ? 'stop_circle' : 'volume_up'} size={22} />} aria-label={speaking ? 'Stop reading aloud' : 'Read this page aloud'} aria-pressed={speaking} onMouseDown={e => e.preventDefault()} onClick={read} />
            </Tooltip>
          )}
          <Box ref={panelRef} role="dialog" aria-label="Accessibility settings" tabIndex={-1}
            sx={{
              display: open ? 'flex' : 'none', flexDirection: 'column', width: 'min(360px, calc(100vw - 32px))', p: 0, overflow: 'hidden', outline: 'none',
              borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: 'var(--m3-surface-container-high)', border: '1px solid var(--m3-outline-variant)', boxShadow: 'var(--m3-elevation-3)', color: 'var(--m3-on-surface)',
              transformOrigin: 'bottom left', animation: `a11yIn ${ease}`, '@keyframes a11yIn': { from: { opacity: 0, transform: 'scale(0.9) translateY(12px)' }, to: { opacity: 1, transform: 'none' } },
              maxHeight: 'min(440px, calc(100vh - 150px))'
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', px: 2.5, pt: 2, pb: 1 }}>
              <Typography variant="h6" component="h2" sx={{ m: 0, fontWeight: 600 }}>Accessibility</Typography>
              <IconButton size="small" aria-label="Close accessibility settings" onClick={() => { setOpen(false); if (fabRef.current) fabRef.current.focus(); }}><GoogleIcon name="close" size={20} /></IconButton>
            </Box>

            {/* The settings scroll here, inside the panel (its scrollbar stays within the rounded corners); the header and Reset stay put */}
            <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.25, px: 2.5, pt: 1, pb: 2, scrollbarWidth: 'thin', scrollbarColor: 'var(--m3-outline) transparent', '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'var(--m3-outline)', borderRadius: 4 }, '&::-webkit-scrollbar-track': { background: 'transparent' } }}>
            <Group title="Reading">
              <Row title="Text size" value={`${s.font}%`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" aria-label="Smaller text" disabled={s.font <= 85} onClick={() => set({ font: Math.max(85, s.font - 5) })}><GoogleIcon name="text_decrease" size={22} /></IconButton>
                  <Box component="input" type="range" min={85} max={150} step={5} value={s.font} aria-label="Text size" aria-valuetext={`${s.font} percent`} onChange={e => set({ font: Number(e.target.value) })} sx={range} />
                  <IconButton size="small" aria-label="Larger text" disabled={s.font >= 150} onClick={() => set({ font: Math.min(150, s.font + 5) })}><GoogleIcon name="text_increase" size={22} /></IconButton>
                </Box>
              </Row>
              <Row title="Text spacing" value={s.spacing ? `${s.spacing}%` : 'Normal'} note="Taller lines and more space between letters and words.">
                <Box component="input" type="range" min={0} max={100} step={5} value={s.spacing} aria-label="Text spacing" aria-valuetext={s.spacing ? `${s.spacing} percent extra spacing` : 'normal spacing'} onChange={e => set({ spacing: Number(e.target.value) })} sx={range} />
              </Row>
              <Switch title="Dyslexia-friendly font" note="Atkinson Hyperlegible, made for low vision and easy letter recognition." checked={s.dyslexia} onChange={v => set({ dyslexia: v })} />
              <Switch title="Underline links" checked={s.links} onChange={v => set({ links: v })} />
              <Switch title="Strong focus ring" note="A thick outline around what the keyboard is on." checked={s.focus} onChange={v => set({ focus: v })} />
              {speechOk && <Switch title="Read aloud" note="Adds a speaker button above this one. It reads your selection, or the page." checked={s.readAloud} onChange={v => set({ readAloud: v })} />}
              {speechOk && s.readAloud && (
                <Row title="Reading speed" value={`${s.speed.toFixed(1)}x`}>
                  <Box component="input" type="range" min={0.6} max={1.6} step={0.1} value={s.speed} aria-label="Reading speed" aria-valuetext={`${s.speed.toFixed(1)} times`} onChange={e => set({ speed: Number(e.target.value) })} sx={range} />
                </Row>
              )}
              <Switch title="Reading ruler" note="Dims the page except a band that follows your pointer and focus." checked={s.ruler} onChange={v => set({ ruler: v })} />
            </Group>

            <Group title="Seeing">
              <Switch title="High contrast" note={s.contrast == null && contrast ? 'On because your device asks for more contrast.' : 'Stronger text and borders; the colours of your chosen theme stay.'} checked={contrast > 0} onChange={v => set({ contrast: v ? 1 : 0 })} />
              <Switch title="Greyscale" note="Takes the colour out of the whole site." checked={s.saturation < 100} onChange={v => set({ saturation: v ? 0 : 100 })} />
              <Switch title="Hide decorations" note="No doodle backgrounds; picture slideshows stay still." checked={s.nodeco} onChange={v => set({ nodeco: v })} />
              <Switch title="Reduce motion" note={`No animations, transitions or smooth scrolling.${s.motion === null ? ' Following your device setting.' : ''}`} checked={reduced} onChange={v => set({ motion: v ? 'reduced' : 'full' })} />
            </Group>

            <Group title="Layout">
              <Switch title="Narrow view" note={`One centred column in the phone layout${s.narrow ? ` (${colWidth(s.narrow)} px wide)` : ''}, for limited peripheral vision.`} checked={s.narrow > 0} onChange={v => set({ narrow: v ? 90 : 0 })} />
              <Switch title="Bigger tap targets" note="Buttons and controls at least 44 by 44 pixels." checked={s.targets} onChange={v => set({ targets: v })} />
            </Group>

            </Box>

            <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', px: 2, py: 1, borderTop: '1px solid var(--m3-outline-variant)', backgroundColor: 'var(--m3-surface-container-high)' }}>
              <Button variant="text" size="small" disabled={!changed} icon={<GoogleIcon name="restart_alt" size={18} />} onClick={() => { stop(); M.resetA11y(); }}>Reset all</Button>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.AccessibilityFab = AccessibilityFab;
})();
